import React, { createElement, useEffect, useRef, useState } from "react";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
    Button,
    ButtonProps,
    Divider,
    ListItemIcon,
    ListItemText,
    SxProps,
    Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
    LoadingButton,
    LoadingButtonProps,
    LoadingIconButton,
} from "./loadingButton";

export interface IMuiMenuItemProps {
    id?: string;
    label?: string | JSX.Element;
    icon?: any;
    onClick?: () => void;
    divider?: boolean;
    disabled?: boolean;
    tooltip?: string;
}

interface IMuiMenuProps {
    id?: string;
    label: any;
    size?: IconButtonProps["size"];
    items: IMuiMenuItemProps[];
    keepOpenAfterSelect?: boolean;
    disabled?: boolean;
    sx?: SxProps;
    buttonSx?: SxProps;
    defaultOpen?: boolean;
    noBox?: boolean;
    iconButton?: boolean;
    buttonColor?: ButtonProps["color"];
    buttonVariant?: ButtonProps["variant"];
    loading?: boolean;
    showTooltips?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function MuiMenu({
    id,
    label,
    size,
    items,
    keepOpenAfterSelect,
    disabled,
    sx,
    buttonSx,
    defaultOpen,
    noBox,
    iconButton = true,
    buttonColor,
    buttonVariant,
    loading,
    showTooltips,
}: IMuiMenuProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const buttonRef = useRef<HTMLButtonElement>();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        event.stopPropagation();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleItemClick = (item: IMuiMenuItemProps) => {
        if (!keepOpenAfterSelect) {
            handleClose();
        }
        if (item.onClick) {
            item.onClick();
        }
    };

    useEffect(() => {
        if (showTooltips) {
            if (open) {
                showTooltips(false);
            } else {
                showTooltips(true);
            }
        }
    }, [open]);

    useEffect(() => {
        if (defaultOpen) {
            buttonRef?.current.click();
        }
    }, []);

    const buttonProps = {
        id,
        loading,
        ref: buttonRef,
        size: size,
        onClick: handleClick,
        disabled: disabled || loading,
        sx: buttonSx,
        children: label,
        color: buttonColor,
    } as LoadingButtonProps;

    if (loading) {
        buttonProps.loadingIndicatorSize = 20;
    }

    if (!iconButton) {
        buttonProps.variant = buttonVariant ?? "contained";
        buttonProps.endIcon = <KeyboardArrowDownIcon />;
    }

    const iconAndMenu = (
        <>
            {createElement(
                loading
                    ? iconButton
                        ? LoadingIconButton
                        : LoadingButton
                    : iconButton
                        ? IconButton
                        : Button,
                buttonProps
            )}
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {items.filter(Boolean).map((i, index) => {
                    const item = !i.divider ? (
                        <MenuItem
                            key={`menu-item-${index}`}
                            id={i.id}
                            onClick={
                                i.disabled
                                    ? undefined
                                    : () => handleItemClick(i)
                            }
                            disabled={i.disabled}
                            sx={{ pointerEvents: "auto !important" }}
                        >
                            {i.icon ? (
                                <ListItemIcon>{i.icon}</ListItemIcon>
                            ) : null}
                            <ListItemText>{i.label}</ListItemText>
                        </MenuItem>
                    ) : null;

                    return i.divider ? (
                        <Divider key={`div-${index}`} />
                    ) : !!i.tooltip ? (
                        <Tooltip key={`${index}-${i.tooltip}`} title={i.tooltip}>
                            {item}
                        </Tooltip>
                    ) : (
                        item
                    );
                })}
            </Menu>
        </>
    );

    return noBox ? iconAndMenu : <Box sx={sx}>{iconAndMenu}</Box>;
}
